/**
 * Per-doc update log (browser) — captures every incremental update to a Y.Doc
 * so the doc's full state can be encoded WITHOUT a synchronous main-thread call.
 *
 * Mirrors `websocket-server/src/yjs/docUpdateLog.js` exactly. See that file
 * for the full architectural rationale. Browser-specific notes below.
 *
 * Why this matters in the browser
 * ───────────────────────────────
 * Synchronous `Y.encodeStateAsUpdate(doc)` on a 64 MiB org-doc takes >1 s —
 * long enough to freeze the UI, drop scroll/click events, and miss WebSocket
 * heartbeats. The chandler-pattern incident showed this directly: a user
 * with an oversized local Y.Doc would freeze on every WS sync because the
 * sync-step-2 encode blocked the main thread.
 *
 * The log lets us ship the update list (not the doc) to the Web Worker,
 * so the encode happens off the UI thread. The seed cost is paid ONCE
 * per doc lifetime (at attach), then steady-state encodes are off-thread.
 *
 * Reference: `/workspace/.cursor/plans/websocket_robustness_refactor_b886ced2.plan.md`
 */

import * as Y from 'yjs';

export interface DocUpdateLogSnapshot {
	updates: Uint8Array[];
	/** Length of the log at snapshot time — see `replace()` doc for usage. */
	generation: number;
}

export interface DocUpdateLog {
	/** Defensive copy of the current updates list + a generation marker. */
	snapshot(): DocUpdateLogSnapshot;
	/**
	 * Replace the log with a consolidated state.
	 *
	 * If `generation` is provided, only entries [0, generation) are
	 * collapsed into `state`. Entries that arrived AFTER the snapshot
	 * (indices [generation, current_length)) are preserved. This is the
	 * concurrency-safe form: use it whenever the consolidated state was
	 * computed from a `snapshot()` and other updates may have arrived
	 * during the encode (always true in practice — Y.js updates can fire
	 * from microtasks).
	 *
	 * Without `generation`, this hard-overwrites the log with a single
	 * entry. Used by tests and by callers that explicitly want a full
	 * reset.
	 */
	replace(state: Uint8Array, generation?: number): void;
	/**
	 * Recovery path for a failed encode: re-seeds the log from the live
	 * doc's CURRENT state via a fresh main-thread encode, discarding
	 * whatever entries `updates` currently holds.
	 *
	 * Use when a caller transferred (and thus detached) the log's buffers
	 * to the Web Worker but the worker failed before `replace()` could run
	 * — e.g. a timeout or a crash. In that case the log is left holding
	 * detached `Uint8Array`s forever; every later `snapshot()` would hand
	 * those same dead buffers to the next caller, which fails the moment
	 * it tries to transfer them again ("ArrayBuffer ... is already
	 * detached"). The live `doc` itself is unaffected by a failed
	 * transfer — only the log's emitted-update COPIES got detached, not
	 * the doc's own structures — so re-encoding directly from `doc` always
	 * recovers a clean log. Pays the same one-time main-thread cost as the
	 * constructor's initial seed, but only on the (rare) failure path.
	 */
	repair(): void;
	length(): number;
	bytes(): number;
	destroy(): void;
	isDestroyed(): boolean;
}

export interface CreateDocUpdateLogOptions {
	/** If true (default), seed the log with the doc's current state via a one-time encode. */
	seedFromCurrentState?: boolean;
	/** Soft cap on buffered updates; informational only — log still records all updates. */
	maxBufferedUpdates?: number;
}

export function createDocUpdateLog(
	doc: Y.Doc,
	options: CreateDocUpdateLogOptions = {},
): DocUpdateLog {
	const { seedFromCurrentState = true } = options;

	if (!doc || typeof doc.on !== 'function') {
		throw new TypeError('createDocUpdateLog: doc must be a Y.Doc');
	}

	let updates: Uint8Array[] = [];
	let bytesAccum = 0;
	let destroyed = false;

	if (seedFromCurrentState) {
		// One-time main-thread cost: extract the current state. Acceptable
		// because it happens ONCE per doc lifetime (typically at WS-provider
		// attach time when the user is already waiting for sync), then never
		// again. Steady-state encodes use the log + Web Worker.
		// eslint-disable-next-line no-restricted-syntax -- documented one-time seed; subsequent encodes go through the worker
		const seed = Y.encodeStateAsUpdateV2(doc);
		updates.push(seed);
		bytesAccum += seed.byteLength;
	}

	const onUpdate = (update: Uint8Array, _origin: unknown, _doc: Y.Doc, _tx: Y.Transaction) => {
		if (destroyed) return;
		updates.push(update);
		bytesAccum += update.byteLength;
	};
	doc.on('updateV2', onUpdate);

	function snapshot(): DocUpdateLogSnapshot {
		// Defensive copy of the array reference + the current length, which
		// acts as a generation marker for racing replaces. The buffers
		// themselves are immutable in practice (Y.js never mutates an
		// emitted update). If the caller transfers them via worker
		// transferList, the copy in `updates` becomes detached too — that's
		// fine because `replace()` overwrites only the prefix.
		return { updates: updates.slice(), generation: updates.length };
	}

	function replace(state: Uint8Array, generation?: number): void {
		if (destroyed) return;
		if (!(state instanceof Uint8Array)) {
			throw new TypeError('replace: state must be a Uint8Array');
		}

		if (typeof generation !== 'number') {
			updates = [state];
			bytesAccum = state.byteLength;
			return;
		}

		// Generation-aware form: collapse entries [0, generation) into the
		// consolidated `state` while preserving entries [generation, end)
		// (which arrived while the worker was encoding). Clamp generation
		// to the current length to handle a destroyed/cleared race.
		const tail = updates.slice(Math.min(generation, updates.length));
		updates = [state, ...tail];
		bytesAccum = state.byteLength;
		for (const u of tail) bytesAccum += u.byteLength;
	}

	function repair(): void {
		if (destroyed) return;
		// eslint-disable-next-line no-restricted-syntax -- documented recovery-only re-seed; see repair() doc above
		const seed = Y.encodeStateAsUpdateV2(doc);
		updates = [seed];
		bytesAccum = seed.byteLength;
	}

	function length(): number {
		return updates.length;
	}

	function bytes(): number {
		return bytesAccum;
	}

	function destroy(): void {
		if (destroyed) return;
		destroyed = true;
		try { doc.off('updateV2', onUpdate); } catch { /* doc may already be destroyed */ }
		updates = [];
		bytesAccum = 0;
	}

	function isDestroyed(): boolean {
		return destroyed;
	}

	void options.maxBufferedUpdates;

	return {
		snapshot,
		replace,
		repair,
		length,
		bytes,
		destroy,
		isDestroyed,
	};
}
